import { useState, useCallback, useEffect, useRef } from 'react';
import { Database, Play, Trash2, Loader2, ChevronDown, ChevronRight } from 'lucide-react';
import { useTableSchema, useExecuteQuery } from '../hooks/useAdminSql';
import type { TableInfo, QueryResult, QueryError } from '../api/admin-sql';
import { JsonPreviewLink } from '../components/common/JsonViewerModal';

export function AdminSqlClientPage() {
  const [sql, setSql] = useState('');
  const [expandedTables, setExpandedTables] = useState<Set<string>>(new Set());
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const { data: tables = [], isLoading: tablesLoading } = useTableSchema();
  const executeQuery = useExecuteQuery();

  const handleExecute = useCallback(() => {
    const trimmed = sql.trim();
    if (!trimmed) return;
    executeQuery.mutate(trimmed);
  }, [sql, executeQuery]);

  const handleClear = useCallback(() => {
    setSql('');
    executeQuery.reset();
  }, [executeQuery]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        handleExecute();
      }
    },
    [handleExecute],
  );

  const handleInsertTable = useCallback((tableName: string) => {
    const quoted = `"${tableName}"`;
    setSql((prev) => {
      if (prev.trim().length === 0) return quoted;
      return prev + ' ' + quoted;
    });
    textareaRef.current?.focus();
  }, []);

  const handleQuickQuery = useCallback(
    (table: TableInfo) => {
      const orderClause = table.hasUpdatedAt ? ` ORDER BY updated_at DESC` : '';
      const query = `SELECT * FROM "${table.tableName}"${orderClause} LIMIT 100`;
      setSql(query);
      executeQuery.mutate(query);
    },
    [executeQuery],
  );

  const toggleTable = useCallback((tableName: string) => {
    setExpandedTables((prev) => {
      const next = new Set(prev);
      if (next.has(tableName)) {
        next.delete(tableName);
      } else {
        next.add(tableName);
      }
      return next;
    });
  }, []);

  // Auto-resize textarea
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = Math.max(textarea.scrollHeight, 96) + 'px';
    }
  }, [sql]);

  return (
    <div>
      <div className="flex items-center gap-2 mb-6">
        <Database size={24} className="text-primary-600" />
        <h1 className="text-2xl font-bold text-gray-900">SQL Client</h1>
      </div>

      <div className="flex gap-4" style={{ minHeight: 'calc(100vh - 200px)' }}>
        {/* Left panel — Schema Browser */}
        <div
          className="bg-white rounded-lg shadow border border-gray-200 overflow-auto shrink-0"
          style={{ width: 280 }}
        >
          <div className="p-3 border-b border-gray-200 bg-gray-50">
            <h2 className="text-sm font-semibold text-gray-700">Schema Browser</h2>
          </div>
          {tablesLoading ? (
            <div className="flex items-center justify-center p-4">
              <Loader2 size={20} className="animate-spin text-gray-400" />
            </div>
          ) : (
            <div className="p-2">
              {tables.map((table) => (
                <div key={table.tableName} className="mb-1">
                  <div className="flex items-center gap-1 group">
                    <button
                      onClick={() => toggleTable(table.tableName)}
                      className="p-0.5 text-gray-400 hover:text-gray-600"
                      aria-label={`Toggle ${table.tableName}`}
                    >
                      {expandedTables.has(table.tableName) ? (
                        <ChevronDown size={14} />
                      ) : (
                        <ChevronRight size={14} />
                      )}
                    </button>
                    <button
                      onClick={() => handleInsertTable(table.tableName)}
                      className="text-sm font-medium text-gray-800 hover:text-primary-600 truncate flex-1 text-left"
                      title={`Click to insert "${table.tableName}"`}
                    >
                      {table.tableName}
                    </button>
                    <span className="text-xs text-gray-400 mr-1">{table.columns.length}</span>
                    <button
                      onClick={() => handleQuickQuery(table)}
                      className="p-0.5 text-gray-400 hover:text-green-600 opacity-0 group-hover:opacity-100 transition-opacity"
                      aria-label={`Quick query ${table.tableName}`}
                      title="SELECT * FROM table"
                    >
                      <Play size={14} />
                    </button>
                  </div>
                  {expandedTables.has(table.tableName) && (
                    <div className="ml-6 mt-1 mb-2">
                      {table.columns.map((col) => (
                        <div
                          key={col.name}
                          className="flex items-center justify-between text-xs py-0.5"
                        >
                          <span className="text-gray-700 truncate">{col.name}</span>
                          <span className="text-gray-400 ml-2 shrink-0">{col.type}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Right panel — Query Editor + Results */}
        <div className="flex-1 flex flex-col gap-4 min-w-0">
          {/* Query Editor */}
          <div className="bg-white rounded-lg shadow border border-gray-200">
            <div className="p-3 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
              <h2 className="text-sm font-semibold text-gray-700">Query Editor</h2>
              <div className="flex gap-2">
                <button
                  onClick={handleClear}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  <Trash2 size={14} />
                  Clear
                </button>
                <button
                  onClick={handleExecute}
                  disabled={executeQuery.isPending || !sql.trim()}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {executeQuery.isPending ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    <Play size={14} />
                  )}
                  Execute
                </button>
              </div>
            </div>
            <div className="p-3">
              <textarea
                ref={textareaRef}
                value={sql}
                onChange={(e) => setSql(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Enter SQL query... (Ctrl+Enter to execute)"
                className="w-full font-mono text-sm border border-gray-300 rounded-md p-3 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none"
                style={{ minHeight: 96 }}
                rows={4}
              />
              <p className="text-xs text-gray-400 mt-1">
                Press Ctrl+Enter to execute. SELECT / INSERT / UPDATE / DELETE are supported; SELECT
                is auto-limited to 100 rows. DROP, TRUNCATE, GRANT and REVOKE are blocked.
              </p>
            </div>
          </div>

          {/* Results */}
          {executeQuery.isPending && (
            <div className="bg-white rounded-lg shadow border border-gray-200 p-8 flex items-center justify-center">
              <Loader2 size={24} className="animate-spin text-primary-600 mr-2" />
              <span className="text-gray-600">Executing query...</span>
            </div>
          )}

          {executeQuery.isError &&
            (() => {
              // Prefer the raw PostgreSQL error returned by the backend over the
              // generic axios "Request failed with status code 400" message.
              const data = (executeQuery.error as { response?: { data?: QueryError } } | undefined)
                ?.response?.data;
              const message =
                data?.message || (executeQuery.error as Error)?.message || 'An error occurred';
              return (
                <div
                  className="bg-red-50 border border-red-200 rounded-lg p-4"
                  data-testid="query-error"
                >
                  <p className="text-sm font-medium text-red-800">Query Error</p>
                  <p className="text-sm text-red-600 mt-1 font-mono whitespace-pre-wrap break-words">
                    {message}
                  </p>
                  {data?.detail && (
                    <p className="text-xs text-red-600 mt-1 font-mono">Detail: {data.detail}</p>
                  )}
                  {data?.hint && (
                    <p className="text-xs text-red-600 mt-1 font-mono">Hint: {data.hint}</p>
                  )}
                  {data?.position && (
                    <p className="text-xs text-red-600 mt-1 font-mono">Position: {data.position}</p>
                  )}
                  {data?.code && (
                    <p className="text-xs text-red-500 mt-1 font-mono">SQLSTATE: {data.code}</p>
                  )}
                </div>
              );
            })()}

          {executeQuery.isSuccess && <QueryResultsTable result={executeQuery.data} />}
        </div>
      </div>
    </div>
  );
}

export function QueryResultsTable({ result }: { result: QueryResult }) {
  return (
    <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
      <div className="p-3 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
        <h2 className="text-sm font-semibold text-gray-700">Results</h2>
        <span className="text-xs text-gray-500" data-testid="row-count">
          {result.rowCount} row{result.rowCount !== 1 ? 's' : ''}
        </span>
      </div>
      <div className="overflow-auto" style={{ maxHeight: '60vh' }}>
        {result.columns.length === 0 ? (
          <div className="p-4 text-sm text-gray-500">Query executed successfully. No results.</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                {result.columns.map((col) => (
                  <th
                    key={col.name}
                    className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap"
                  >
                    {col.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {result.rows.map((row, i) => (
                <tr
                  key={i}
                  className={`border-b border-gray-100 ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
                >
                  {result.columns.map((col) => (
                    <td key={col.name} className="px-3 py-2 font-mono text-xs whitespace-nowrap">
                      {row[col.name] === null || row[col.name] === undefined ? (
                        <span className="italic text-gray-400">NULL</span>
                      ) : typeof row[col.name] === 'object' ? (
                        <JsonPreviewLink data={row[col.name]} />
                      ) : (
                        String(row[col.name])
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
